import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Header } from '@/components/Header';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { 
  LayoutDashboard, BookOpen, Settings, Gift, Save, Loader2,
  Zap, Headphones, Users, Video, Plus, Trash2, Edit2, X, GraduationCap
} from 'lucide-react';

const AdminSidebar = () => {
  return (
    <aside className="fixed left-0 top-0 h-screen w-64 admin-sidebar border-r border-[#2A2A2A] pt-20 hidden lg:block">
      <nav className="p-4 space-y-2">
        <Link to="/admin" className="flex items-center gap-3 px-4 py-3 rounded-lg text-[#A1A1AA] hover:bg-white/5 hover:text-[#F5F5DC] transition-colors">
          <LayoutDashboard size={20} />
          <span>Dashboard</span>
        </Link>
        <Link to="/admin/courses" className="flex items-center gap-3 px-4 py-3 rounded-lg text-[#A1A1AA] hover:bg-white/5 hover:text-[#F5F5DC] transition-colors">
          <BookOpen size={20} />
          <span>Course Guide</span>
        </Link>
        <Link to="/admin/settings" className="flex items-center gap-3 px-4 py-3 rounded-lg text-[#A1A1AA] hover:bg-white/5 hover:text-[#F5F5DC] transition-colors">
          <Settings size={20} />
          <span>Class Settings</span>
        </Link>
        <Link to="/admin/perks" className="flex items-center gap-3 px-4 py-3 rounded-lg bg-[#D4AF37]/10 text-[#D4AF37]">
          <Gift size={20} />
          <span>Perks & Tiers</span>
        </Link>
        <Link to="/admin/training" className="flex items-center gap-3 px-4 py-3 rounded-lg text-[#A1A1AA] hover:bg-white/5 hover:text-[#F5F5DC] transition-colors">
          <GraduationCap size={20} />
          <span>Staff Training</span>
        </Link>
      </nav>
    </aside>
  );
};

const iconOptions = [
  { name: 'Zap', icon: Zap },
  { name: 'Headphones', icon: Headphones },
  { name: 'Users', icon: Users },
  { name: 'Video', icon: Video },
  { name: 'Gift', icon: Gift },
];

const defaultPerks = [
  {
    id: '1',
    title: 'Productivity Tools',
    description: 'Curated tools and resources to help you practice smarter, not just harder. Get organized and track your progress.',
    icon: 'Zap'
  },
  {
    id: '2',
    title: 'Practice Apps',
    description: 'Access to the best practice apps with guidance on how to actually use them. Modern learning meets traditional discipline.',
    icon: 'Headphones'
  },
  {
    id: '3',
    title: 'Doubt Clearing',
    description: 'Stuck on something? Get your questions answered. No waiting until your next lesson to move forward.',
    icon: 'Users'
  },
  {
    id: '4',
    title: 'In-Person Studio Access',
    description: 'Train at our Bangalore studio with professional recording equipment. Learn production hands-on, not just in theory.',
    icon: 'Video'
  }
];

export const AdminPerks = () => {
  const [perks, setPerks] = useState([]);
  const [editingPerk, setEditingPerk] = useState(null);
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [saving, setSaving] = useState(false);

  const [newPerk, setNewPerk] = useState({
    title: '',
    description: '',
    icon: 'Zap'
  });

  useEffect(() => {
    // Load perks from localStorage or use defaults
    const savedPerks = localStorage.getItem('nsm_perks');
    if (savedPerks) {
      setPerks(JSON.parse(savedPerks));
    } else {
      setPerks(defaultPerks);
    }
  }, []);

  const savePerks = (updatedPerks) => {
    localStorage.setItem('nsm_perks', JSON.stringify(updatedPerks));
    setPerks(updatedPerks);
    toast.success('Perks saved successfully');
  };

  const handleAddPerk = () => {
    if (!newPerk.title || !newPerk.description) {
      toast.error('Please fill in all fields');
      return;
    }

    const perk = {
      id: String(Date.now()),
      ...newPerk
    };

    savePerks([...perks, perk]);
    setNewPerk({ title: '', description: '', icon: 'Zap' });
    setIsAddingNew(false);
  };

  const handleUpdatePerk = () => {
    if (!editingPerk.title || !editingPerk.description) {
      toast.error('Please fill in all fields');
      return;
    }

    const updatedPerks = perks.map(p => 
      p.id === editingPerk.id ? editingPerk : p
    );
    savePerks(updatedPerks);
    setEditingPerk(null);
  };

  const handleDeletePerk = (perkId) => {
    if (!window.confirm('Are you sure you want to delete this perk?')) return;
    
    const updatedPerks = perks.filter(p => p.id !== perkId);
    savePerks(updatedPerks);
  };

  const getIconComponent = (iconName) => {
    const found = iconOptions.find(i => i.name === iconName);
    return found ? found.icon : Zap;
  };

  return (
    <div className="min-h-screen bg-[#050505]">
      <Header variant="solid" />
      <AdminSidebar />
      
      <main className="lg:ml-64 pt-20 p-6">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="font-heading text-3xl text-[#EDEDED] mb-2">Perks & Tiers</h1>
              <p className="text-[#A1A1AA]">
                Customize the perks displayed on the public website
              </p>
            </div>
            
            <Button
              onClick={() => setIsAddingNew(true)}
              className="btn-gold"
              disabled={isAddingNew}
              data-testid="btn-add-perk"
            >
              <Plus size={16} className="mr-2" />
              Add Perk
            </Button>
          </div>

          {/* Add New Perk Form */}
          {isAddingNew && (
            <div className="bg-[#121212] border border-[#D4AF37] rounded-xl p-6 mb-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-heading text-xl text-[#F5F5DC]">Add New Perk</h2>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setIsAddingNew(false)}
                  className="text-[#A1A1AA]"
                >
                  <X size={18} />
                </Button>
              </div>
              
              <div className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-[#A1A1AA]">Title</Label>
                    <Input
                      value={newPerk.title}
                      onChange={(e) => setNewPerk({ ...newPerk, title: e.target.value })}
                      className="bg-black/50 border-[#2A2A2A] text-[#EDEDED]"
                      placeholder="e.g., Productivity Tools"
                      data-testid="input-new-title"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label className="text-[#A1A1AA]">Icon</Label>
                    <div className="flex gap-2">
                      {iconOptions.map(({ name, icon: Icon }) => (
                        <button
                          key={name}
                          onClick={() => setNewPerk({ ...newPerk, icon: name })}
                          className={`p-3 rounded-lg border transition-colors ${
                            newPerk.icon === name
                              ? 'border-[#D4AF37] bg-[#D4AF37]/10'
                              : 'border-[#2A2A2A] hover:border-[#D4AF37]/50'
                          }`}
                        >
                          <Icon size={20} className={newPerk.icon === name ? 'text-[#D4AF37]' : 'text-[#A1A1AA]'} />
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label className="text-[#A1A1AA]">Description</Label>
                  <Textarea
                    value={newPerk.description}
                    onChange={(e) => setNewPerk({ ...newPerk, description: e.target.value })}
                    className="bg-black/50 border-[#2A2A2A] text-[#EDEDED]"
                    placeholder="Describe what this perk offers..."
                    data-testid="input-new-description"
                  />
                </div>
                
                <div className="flex justify-end gap-3">
                  <Button
                    variant="outline"
                    onClick={() => setIsAddingNew(false)}
                    className="border-[#2A2A2A] text-[#A1A1AA]"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleAddPerk}
                    className="btn-gold"
                    data-testid="btn-save-new"
                  >
                    Add Perk
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Perks List */}
          <div className="space-y-4">
            {perks.map((perk) => {
              const IconComponent = getIconComponent(perk.icon);
              const isEditing = editingPerk?.id === perk.id;
              
              return (
                <div 
                  key={perk.id}
                  className={`bg-[#121212] border rounded-xl p-6 ${
                    isEditing ? 'border-[#D4AF37]' : 'border-[#2A2A2A]'
                  }`}
                >
                  {isEditing ? (
                    <div className="space-y-4">
                      <div className="grid md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label className="text-[#A1A1AA]">Title</Label>
                          <Input
                            value={editingPerk.title}
                            onChange={(e) => setEditingPerk({ ...editingPerk, title: e.target.value })}
                            className="bg-black/50 border-[#2A2A2A] text-[#EDEDED]"
                          />
                        </div>
                        
                        <div className="space-y-2">
                          <Label className="text-[#A1A1AA]">Icon</Label>
                          <div className="flex gap-2">
                            {iconOptions.map(({ name, icon: Icon }) => (
                              <button
                                key={name}
                                onClick={() => setEditingPerk({ ...editingPerk, icon: name })}
                                className={`p-3 rounded-lg border transition-colors ${
                                  editingPerk.icon === name
                                    ? 'border-[#D4AF37] bg-[#D4AF37]/10'
                                    : 'border-[#2A2A2A] hover:border-[#D4AF37]/50'
                                }`}
                              >
                                <Icon size={20} className={editingPerk.icon === name ? 'text-[#D4AF37]' : 'text-[#A1A1AA]'} />
                              </button>
                            ))}
                          </div>
                        </div>
                      </div>
                      
                      <div className="space-y-2">
                        <Label className="text-[#A1A1AA]">Description</Label>
                        <Textarea
                          value={editingPerk.description}
                          onChange={(e) => setEditingPerk({ ...editingPerk, description: e.target.value })}
                          className="bg-black/50 border-[#2A2A2A] text-[#EDEDED]"
                        />
                      </div>
                      
                      <div className="flex justify-end gap-3">
                        <Button
                          variant="outline"
                          onClick={() => setEditingPerk(null)}
                          className="border-[#2A2A2A] text-[#A1A1AA]"
                        >
                          Cancel
                        </Button>
                        <Button
                          onClick={handleUpdatePerk}
                          className="btn-gold"
                        >
                          <Save size={16} className="mr-2" />
                          Save Changes
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-start gap-4">
                      <div className="w-14 h-14 rounded-xl bg-[#D4AF37]/10 flex items-center justify-center flex-shrink-0">
                        <IconComponent className="text-[#D4AF37]" size={24} />
                      </div>
                      
                      <div className="flex-1">
                        <h3 className="font-heading text-xl text-[#F5F5DC] mb-2">{perk.title}</h3>
                        <p className="text-[#A1A1AA]">{perk.description}</p>
                      </div>
                      
                      <div className="flex gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setEditingPerk(perk)}
                          className="text-[#A1A1AA] hover:text-[#D4AF37]"
                          data-testid={`edit-perk-${perk.id}`}
                        >
                          <Edit2 size={16} />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeletePerk(perk.id)}
                          className="text-[#A1A1AA] hover:text-red-400"
                          data-testid={`delete-perk-${perk.id}`}
                        >
                          <Trash2 size={16} />
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {perks.length === 0 && (
            <div className="text-center py-12 text-[#A1A1AA]">
              <Gift size={40} className="mx-auto mb-4 opacity-50" />
              <p>No perks configured yet. Add your first perk above.</p>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default AdminPerks;
